import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import { getClient, getActiveSpan, getRootSpan, spanToJSON, logger, browserPerformanceTimeOrigin, getCurrentScope, htmlTreeAsString, dropUndefinedKeys, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE } from '@sentry/core';
import { DEBUG_BUILD } from '../debug-build.js';
import { addClsInstrumentationHandler } from './instrument.js';
import { msToSec, startStandaloneWebVitalSpan } from './utils.js';
import { onHidden } from './web-vitals/lib/onHidden.js';

/**
 * Starts tracking the Cumulative Layout Shift on the current page and collects the value once
 *
 * - the page visibility is hidden
 * - a navigation span is started (to stop CLS measurement for SPA soft navigations)
 *
 * Once either of these events triggers, the CLS value is sent as a standalone span and we stop
 * measuring CLS.
 */
function trackClsAsStandaloneSpan() {
  var standaloneCLsValue = 0;
  var standaloneClsEntry;
  var pageloadSpanId;
  if (!supportsLayoutShift()) {
    return;
  }
  var sentSpan = false;
  function _collectClsOnce() {
    if (sentSpan) {
      return;
    }
    sentSpan = true;
    if (pageloadSpanId) {
      sendStandaloneClsSpan(standaloneCLsValue, standaloneClsEntry, pageloadSpanId);
    }
    cleanupClsHandler();
  }
  var cleanupClsHandler = addClsInstrumentationHandler(function (_ref) {
    var metric = _ref.metric;
    var entry = metric.entries[metric.entries.length - 1];
    if (!entry) {
      return;
    }
    standaloneCLsValue = metric.value;
    standaloneClsEntry = entry;
  }, true);

  // use pagehide event from web-vitals
  onHidden(function () {
    _collectClsOnce();
  });

  // Since the call chain of this function is synchronous and evaluates before the SDK client is created,
  // we need to wait with subscribing to a client hook until the client is created. Therefore, we defer
  // to the next tick after the SDK setup.
  setTimeout(function () {
    var client = getClient();
    if (!client) {
      return;
    }
    var unsubscribeStartNavigation = client.on('startNavigationSpan', function () {
      _collectClsOnce();
      unsubscribeStartNavigation && unsubscribeStartNavigation();
    });
    var activeSpan = getActiveSpan();
    var rootSpan = activeSpan && getRootSpan(activeSpan);
    var spanJSON = rootSpan && spanToJSON(rootSpan);
    if (spanJSON && spanJSON.op === 'pageload') {
      pageloadSpanId = rootSpan.spanContext().spanId;
    }
  }, 0);
}
function sendStandaloneClsSpan(clsValue, entry, pageloadSpanId) {
  var _dropUndefinedKeys;
  DEBUG_BUILD && logger.log("Sending CLS span (".concat(clsValue, ")"));
  var startTime = msToSec((browserPerformanceTimeOrigin || 0) + (entry && entry.startTime || 0));
  var routeName = getCurrentScope().getScopeData().transactionName;
  var name = entry ? htmlTreeAsString(entry.sources[0] && entry.sources[0].node) : 'Layout shift';
  var attributes = dropUndefinedKeys((_dropUndefinedKeys = {}, _defineProperty(_dropUndefinedKeys, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, 'auto.http.browser.cls'), _defineProperty(_dropUndefinedKeys, SEMANTIC_ATTRIBUTE_SENTRY_OP, 'ui.webvital.cls'), _defineProperty(_dropUndefinedKeys, SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME, entry && entry.duration || 0), _defineProperty(_dropUndefinedKeys, 'sentry.pageload.span_id', pageloadSpanId), _dropUndefinedKeys));
  var span = startStandaloneWebVitalSpan({
    name: name,
    transaction: routeName,
    attributes: attributes,
    startTime: startTime
  });
  if (span) {
    var _span$addEvent;
    span.addEvent('cls', (_span$addEvent = {}, _defineProperty(_span$addEvent, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT, ''), _defineProperty(_span$addEvent, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE, clsValue), _span$addEvent));

    // LayoutShift performance entries always have a duration of 0, so we don't need to add `entry.duration` here
    // see: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceEntry/duration
    span.end(startTime);
  }
}
function supportsLayoutShift() {
  try {
    return PerformanceObserver.supportedEntryTypes.includes('layout-shift');
  } catch (e) {
    return false;
  }
}
export { trackClsAsStandaloneSpan };